import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Package, AlertTriangle, TrendingDown, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const THRDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [districtDelivery, setDistrictDelivery] = useState([]);
  const [otpAuth, setOtpAuth] = useState(null);
  const [leakageAlerts, setLeakageAlerts] = useState(null);
  const [rationComparison, setRationComparison] = useState(null);
  const [topBottom, setTopBottom] = useState(null);
  const [selectedRation, setSelectedRation] = useState('balshakti');
  const [threshold, setThreshold] = useState(80);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [threshold, selectedRation]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [overviewRes, deliveryRes, otpRes, leakageRes, comparisonRes, topBottomRes] = await Promise.all([
        axios.get(`${API}/api/dashboards/thr/overview`),
        axios.get(`${API}/api/dashboards/thr/district-delivery?limit=50`),
        axios.get(`${API}/api/dashboards/thr/otp-authentication`),
        axios.get(`${API}/api/dashboards/thr/leakage-detection?threshold=${threshold}`),
        axios.get(`${API}/api/dashboards/thr/ration-comparison`),
        axios.get(`${API}/api/dashboards/thr/top-bottom?ration_type=${selectedRation}&limit=5`)
      ]);

      setOverview(overviewRes.data);
      setDistrictDelivery(deliveryRes.data);
      setOtpAuth(otpRes.data);
      setLeakageAlerts(leakageRes.data);
      setRationComparison(comparisonRes.data);
      setTopBottom(topBottomRes.data);
    } catch (error) {
      console.error('Error fetching THR dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDeliveryColor = (pct) => {
    if (pct >= 80) return '#22c55e';
    if (pct >= 60) return '#facc15';
    return '#ef4444';
  };

  const KPICard = ({ title, scheduled, delivered, deliveryPct, otpPct, icon: Icon, color }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
        <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
        <div className="space-y-2">
          <div>
            <p className="text-3xl font-bold" style={{ color: getDeliveryColor(deliveryPct) }}>
              {deliveryPct}%
            </p>
            <p className="text-sm text-gray-500">
              {delivered?.toLocaleString()} / {scheduled?.toLocaleString()} delivered
            </p>
          </div>
          <div className="pt-2 border-t">
            <p className="text-xs text-gray-500">OTP Authentication</p>
            <p className="text-lg font-semibold">{otpPct}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading THR dashboard data...</p>
        </div>
      </div>
    );
  }

  // Prepare data for stacked bar chart
  const stackedBarData = districtDelivery.slice(0, 15).map(d => ({
    district: d.district,
    balshakti: d.balshakti.delivery_pct,
    matru: d.matru.delivery_pct,
    purna: d.purna.delivery_pct
  }));

  // Prepare delivery gap data
  const deliveryGapData = [
    { 
      name: 'Balshakti', 
      scheduled: overview?.balshakti.scheduled || 0, 
      delivered: overview?.balshakti.delivered || 0,
      gap: (overview?.balshakti.scheduled || 0) - (overview?.balshakti.delivered || 0)
    },
    { 
      name: 'Matru', 
      scheduled: overview?.matru.scheduled || 0, 
      delivered: overview?.matru.delivered || 0,
      gap: (overview?.matru.scheduled || 0) - (overview?.matru.delivered || 0)
    },
    { 
      name: 'Purna', 
      scheduled: overview?.purna.scheduled || 0, 
      delivered: overview?.purna.delivered || 0,
      gap: (overview?.purna.scheduled || 0) - (overview?.purna.delivered || 0)
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 pt-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">THR Distribution & Leakages Prevention</h1>
          <p className="text-gray-600">Monitor Take Home Ration delivery across 3 beneficiary types: Children (Balshakti), Pregnant Women (Matru), Adolescent Girls (Purna)</p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <Select value={selectedRation} onValueChange={setSelectedRation}>
            <SelectTrigger className="w-64 bg-white">
              <SelectValue placeholder="Select Ration Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="balshakti">Balshakti (500mg)</SelectItem>
              <SelectItem value="matru">Matru Shakti (1kg)</SelectItem>
              <SelectItem value="purna">Purna Shakti (1kg)</SelectItem>
            </SelectContent>
          </Select>

          <Select value={threshold.toString()} onValueChange={(val) => setThreshold(parseInt(val))}>
            <SelectTrigger className="w-48 bg-white">
              <SelectValue placeholder="Threshold" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="60">60% Threshold</SelectItem>
              <SelectItem value="70">70% Threshold</SelectItem>
              <SelectItem value="80">80% Threshold</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            title="Balshakti (Children 500mg)"
            scheduled={overview?.balshakti.scheduled}
            delivered={overview?.balshakti.delivered}
            deliveryPct={overview?.balshakti.delivery_pct}
            otpPct={overview?.balshakti.otp_pct}
            icon={Package}
            color="blue"
          />
          <KPICard
            title="Matru Shakti (Pregnant Women 1kg)"
            scheduled={overview?.matru.scheduled}
            delivered={overview?.matru.delivered}
            deliveryPct={overview?.matru.delivery_pct}
            otpPct={overview?.matru.otp_pct}
            icon={Package}
            color="pink"
          />
          <KPICard
            title="Purna Shakti (Adolescent Girls 1kg)"
            scheduled={overview?.purna.scheduled}
            delivered={overview?.purna.delivered}
            deliveryPct={overview?.purna.delivery_pct}
            otpPct={overview?.purna.otp_pct}
            icon={Package}
            color="purple"
          />
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 rounded-full bg-red-100">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">Districts Below Target</h3>
              <p className="text-3xl font-bold text-red-600">{overview?.districts_below_threshold}</p>
              <p className="text-sm text-gray-500 mt-1">out of {overview?.total_districts} districts</p>
              <p className="text-xs text-red-600 mt-2">&lt; {threshold}% delivery</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="comparison">Ration Comparison</TabsTrigger>
            <TabsTrigger value="leakage">Leakage Detection</TabsTrigger>
            <TabsTrigger value="otp">OTP Authentication</TabsTrigger>
            <TabsTrigger value="data">Data Table</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Stacked Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>District-wise Delivery by Ration Type</CardTitle>
                  <CardDescription>Top 15 districts - delivery % for all 3 ration types</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={stackedBarData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="district" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="balshakti" fill="#3b82f6" name="Balshakti %" stackId="a" />
                      <Bar dataKey="matru" fill="#ec4899" name="Matru %" stackId="a" />
                      <Bar dataKey="purna" fill="#a855f7" name="Purna %" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Delivery Gap Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Scheduled vs Delivered Gap</CardTitle>
                  <CardDescription>Identify delivery shortfall across ration types</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={deliveryGapData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="scheduled" fill="#94a3b8" name="Scheduled" />
                      <Bar dataKey="delivered" fill="#22c55e" name="Delivered" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {deliveryGapData.map((item, idx) => (
                      <div key={idx} className="p-3 bg-red-50 rounded">
                        <p className="text-xs text-gray-600">{item.name} Gap</p>
                        <p className="text-lg font-bold text-red-600">{item.gap.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top and Bottom Performers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top 5 Performers - {selectedRation}</CardTitle>
                  <CardDescription>Districts with highest delivery rates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topBottom?.top_performers?.map((district, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium">{district.district}</p>
                          <p className="text-sm text-gray-600">{district.delivered.toLocaleString()} / {district.scheduled.toLocaleString()}</p>
                        </div>
                        <div className="text-2xl font-bold text-green-700">{district.delivery_pct}%</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Bottom 5 Performers - {selectedRation}</CardTitle>
                  <CardDescription>Districts needing immediate intervention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topBottom?.bottom_performers?.map((district, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{district.district}</p>
                          <p className="text-sm text-gray-600">{district.delivered.toLocaleString()} / {district.scheduled.toLocaleString()}</p>
                        </div>
                        <div className="text-2xl font-bold text-red-700">{district.delivery_pct}%</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Ration Comparison Tab */}
          <TabsContent value="comparison">
            <Card>
              <CardHeader>
                <CardTitle>Ration Type Performance Comparison</CardTitle>
                <CardDescription>Compare delivery performance across all 3 ration types</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {rationComparison?.comparison?.map((ration, idx) => (
                    <div key={idx} className="p-6 border rounded-lg">
                      <h3 className="font-semibold text-lg mb-4">{ration.ration_type}</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-600">Avg Delivery %</p>
                          <p className="text-3xl font-bold" style={{ color: getDeliveryColor(ration.avg_delivery_pct) }}>
                            {ration.avg_delivery_pct}%
                          </p>
                        </div>
                        <div className="pt-3 border-t">
                          <p className="text-sm text-gray-600">Districts ≥80%</p>
                          <p className="text-2xl font-semibold text-green-600">{ration.districts_above_80}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Districts &lt;80%</p>
                          <p className="text-2xl font-semibold text-red-600">{ration.districts_below_80}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leakage Detection Tab */}
          <TabsContent value="leakage">
            <Card>
              <CardHeader>
                <CardTitle>Leakage Alerts (Below {threshold}% Threshold)</CardTitle>
                <CardDescription>{leakageAlerts?.total_districts_with_alerts} districts with potential leakages detected</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {leakageAlerts?.alerts?.slice(0, 10).map((alert, idx) => (
                    <div key={idx} className="p-4 border border-red-300 bg-red-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-lg">{alert.district}</h4>
                        <span className="px-3 py-1 bg-red-600 text-white rounded-full text-sm">
                          {alert.alert_count} Alert{alert.alert_count > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {alert.alerts.map((a, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-white rounded">
                            <div>
                              <p className="font-medium">{a.ration_type}</p>
                              <p className="text-sm text-gray-600">
                                {a.delivered.toLocaleString()} / {a.scheduled.toLocaleString()} ({a.delivery_pct}%)
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Gap</p>
                              <p className="text-lg font-bold text-red-600">{a.gap.toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* OTP Authentication Tab */}
          <TabsContent value="otp">
            <Card>
              <CardHeader>
                <CardTitle>OTP Authentication for Leakage Prevention</CardTitle>
                <CardDescription>
                  High: ≥80% | Medium: 50-79% | Low: &lt;50% OTP usage
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6 grid grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">High OTP Usage</p>
                    <p className="text-3xl font-bold text-green-600">{otpAuth?.summary.high_otp}</p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Medium OTP Usage</p>
                    <p className="text-3xl font-bold text-yellow-600">{otpAuth?.summary.medium_otp}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Low OTP Usage</p>
                    <p className="text-3xl font-bold text-red-600">{otpAuth?.summary.low_otp}</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg OTP %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balshakti</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matru</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purna</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {otpAuth?.districts?.slice(0, 20).map((district, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{district.district}</td>
                          <td className="px-4 py-3 font-bold">{district.avg_otp_pct}%</td>
                          <td className="px-4 py-3">{district.balshakti_otp}%</td>
                          <td className="px-4 py-3">{district.matru_otp}%</td>
                          <td className="px-4 py-3">{district.purna_otp}%</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              district.rating === 'High' ? 'bg-green-100 text-green-800' :
                              district.rating === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {district.rating}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Table Tab */}
          <TabsContent value="data">
            <Card>
              <CardHeader>
                <CardTitle>District-wise Complete Data</CardTitle>
                <CardDescription>All metrics for all districts and ration types</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">AWCs</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balshakti %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matru %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purna %</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg OTP %</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {districtDelivery.map((district, idx) => {
                        const avgOtp = (district.balshakti.otp_pct + district.matru.otp_pct + district.purna.otp_pct) / 3;
                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium">{district.district}</td>
                            <td className="px-4 py-3">{district.anganwadi_count}</td>
                            <td className="px-4 py-3">
                              <span style={{ color: getDeliveryColor(district.balshakti.delivery_pct) }} className="font-semibold">
                                {district.balshakti.delivery_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span style={{ color: getDeliveryColor(district.matru.delivery_pct) }} className="font-semibold">
                                {district.matru.delivery_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span style={{ color: getDeliveryColor(district.purna.delivery_pct) }} className="font-semibold">
                                {district.purna.delivery_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-3">{avgOtp.toFixed(2)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  </div>
  );
};

export default THRDashboard;
